import { useState, useEffect, useRef } from 'react';
import { trackContentEvent } from '@/analytics/analytics';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Volume2, Headphones, Search, Clock, BookOpen, GraduationCap, School, FileText, ChevronDown, ChevronUp, Download, Sparkles } from 'lucide-react';
import BudleeAIIcon from '@/components/BudleeAIIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { curiosityAPI } from '../services/curiosityAPI';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import FloatingPointsAnimation, { useFloatingPointsAnimation } from '@/components/FloatingPointsAnimation';
import StatsBar from '@/components/StatsBar';
import { toast } from 'sonner';

/**
 * Get MIME type for audio file based on extension
 * Helps browser determine if format is supported
 */
const getAudioMimeType = (audioUrl) => {
  if (!audioUrl) return 'audio/mpeg';

  const url = audioUrl.toLowerCase();
  if (url.endsWith('.mp3')) return 'audio/mpeg';
  if (url.endsWith('.m4a') || url.endsWith('.mp4')) return 'audio/mp4';
  if (url.endsWith('.webm')) return 'audio/webm';
  if (url.endsWith('.ogg') || url.endsWith('.oga')) return 'audio/ogg';
  if (url.endsWith('.opus')) return 'audio/opus';
  if (url.endsWith('.wav')) return 'audio/wav';
  if (url.endsWith('.aac')) return 'audio/aac';
  if (url.endsWith('.flac')) return 'audio/flac';

  // Default to MP3 if unknown
  return 'audio/mpeg';
};

/**
 * Audio Player Component for Podcast Content
 * 
 * Features:
 * - HTML5 audio player for podcast playback
 * - Transcript display and search
 * - Time tracking and completion percentage
 * - Auto-save progress
 */
export default function AudioPlayer() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [transcriptPanelOpen, setTranscriptPanelOpen] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');
  const [isWatched, setIsWatched] = useState(false);
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const timeTrackingIntervalRef = useRef(null);
  const playPromiseRef = useRef(null);
  const isMountedRef = useRef(true);
  const { selectedSubjectId, selectedChapterId } = useSubjectChapter();
  const { refreshPoints } = usePoints();
  const { addAnimation, renderAnimations } = useFloatingPointsAnimation();
  const statsBarRef = useRef(null);
  const audioPlayerRef = useRef(null);

  const isSmallScreen = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  useEffect(() => {
    if (contentId) {
      loadContent();
    }
  }, [contentId]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (timeTrackingIntervalRef.current) {
        clearInterval(timeTrackingIntervalRef.current);
      }
      if (audioRef.current) {
        // Pause audio first
        audioRef.current.pause();
        // Cancel any pending play promise
        if (playPromiseRef.current) {
          playPromiseRef.current.catch(() => {
            // Ignore errors from cancelled play promises
          });
          playPromiseRef.current = null;
        }
        // Remove event listeners and clean up
        audioRef.current.src = '';
        audioRef.current.load();
        audioRef.current = null;
      }
    };
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const contentData = await curiosityAPI.getContent(contentId);
      setContent(contentData);

      // Check if content is already watched (from API response or fallback to separate call)
      console.log('📻 Loading podcast content - is_watched from API:', contentData.is_watched);
      if (contentData.is_watched !== undefined) {
        setIsWatched(contentData.is_watched);
        console.log('✅ Set isWatched from API response:', contentData.is_watched);
      } else {
        // Fallback: check via interactions API if is_watched not in response
        console.log('⚠️ is_watched not in API response, checking interactions API...');
        try {
          const interactions = await curiosityAPI.getInteractions({ content: parseInt(contentId) });
          const hasCompleted = interactions.some(interaction => interaction.interaction_type === 'completed');
          console.log('📊 Interactions check - hasCompleted:', hasCompleted, 'interactions:', interactions);
          setIsWatched(hasCompleted);
        } catch (err) {
          console.warn('Failed to check watched status:', err);
          setIsWatched(false);
        }
      }

      // Log audio URL for debugging
      if (contentData.content_type === 'podcast') {
        console.log('Audio URL:', contentData.audio_url);
        console.log('Audio filepath:', contentData.audio_filepath);
        if (!contentData.audio_url) {
          console.warn('Audio URL is missing for podcast content. Please check backend configuration.');
        }
      }

      // Load transcript if available
      if (contentData.transcript) {
        setTranscript(contentData.transcript);
        setTranscriptText(contentData.transcript.transcript_text || '');
      }

      // Track "viewed" interaction (non-blocking)
      curiosityAPI.recordInteraction({
        content: parseInt(contentId),
        interaction_type: 'viewed',
      }).catch(err => console.warn('Failed to record interaction:', err));
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAudioError = (e) => {
    // Get the audio element from the event target
    const audio = e?.target || e?.nativeEvent?.target || audioRef.current;

    if (!audio) {
      console.error('Audio playback error: Audio element not found');
      toast.error('Audio playback error: Unable to access audio element');
      return;
    }

    // Check if audio has error property
    if (audio.error) {
      const errorCode = audio.error.code;
      const errorMessages = {
        1: 'MEDIA_ERR_ABORTED - The user aborted the audio',
        2: 'MEDIA_ERR_NETWORK - A network error occurred',
        3: 'MEDIA_ERR_DECODE - The audio is corrupted or format not supported',
        4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - The audio format is not supported or the source could not be found'
      };

      const errorMessage = errorMessages[errorCode] || audio.error.message || 'Unknown error';
      const audioUrl = audio.src || content?.audio_url || 'Unknown URL';

      console.error('Audio playback error details:', {
        errorCode,
        errorMessage,
        audioUrl,
        audioElement: audio
      });

      // Show user-friendly error message using toast instead of alert
      toast.error(`Failed to load audio: ${errorMessage}`, {
        description: `URL: ${audioUrl}`,
        duration: 5000
      });
    } else {
      // Fallback error handling when error property is not available
      const audioUrl = audio.src || content?.audio_url || 'Unknown URL';
      console.error('Audio playback error (no error code):', {
        event: e,
        audioUrl,
        audioElement: audio
      });

      toast.error('Failed to load audio. Please check if the audio file is accessible.', {
        description: `URL: ${audioUrl}`,
        duration: 5000
      });
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !content?.audio_url) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      if (duration > 0) {
        const percentage = (audio.currentTime / duration) * 100;
        setCompletionPercentage(percentage);
      }
    };

    const updateDuration = () => {
      if (audio.duration && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
      }
      stopTimeTracking();
      // Only track completion and award XP if not already watched
      if (!isWatched) {
        console.log('🎧 Podcast ended - recording completion interaction for content:', contentId);
        // Track completion
        curiosityAPI.recordInteraction({
          content: parseInt(contentId),
          interaction_type: 'completed',
          completion_percentage: 100,
        }).then(async (response) => {
          console.log('✅ Completion interaction recorded:', response);
          setIsWatched(true);
          // Refresh points after recording completion (backend signal will award XP)
          await refreshPoints();

          // Trigger floating animation for XP earned (more noticeable in center)
          setTimeout(() => {
            const targetPosition = statsBarRef.current
              ? (() => {
                const rect = statsBarRef.current.getBoundingClientRect();
                return {
                  x: rect.left + rect.width / 2,
                  y: rect.top + rect.height / 2,
                };
              })()
              : { x: window.innerWidth - 100, y: 60 };

            // Start from center of page for more visibility
            const startPosition = {
              x: window.innerWidth / 2,
              y: window.innerHeight / 2,
            };

            // Determine XP amount based on tag_type
            const xpAmount = content?.tag_type === 'core' ? 5 : content?.tag_type === 'deep' ? 8 : 3;
            addAnimation('xp', xpAmount, startPosition, targetPosition);
          }, 50);
        }).catch(err => console.warn('Failed to record completion:', err));
      }
        try {
          trackContentEvent('content_complete', {
            content_id: contentId ? parseInt(contentId) : null,
            content_type: 'podcast',
            content_title: content?.title || null,
            completion_percentage: 100,
          });
        } catch (e) {}
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [content?.audio_url, duration, contentId]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Cancel any pending play promise before pausing
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {
          // Ignore errors from cancelled play promises
        });
        playPromiseRef.current = null;
      }
      audio.pause();
      setIsPlaying(false);
      stopTimeTracking();
      try {
        trackContentEvent('content_pause', {
          content_id: contentId ? parseInt(contentId) : null,
          content_type: 'podcast',
          content_title: content?.title || null,
          subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
          chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
        });
      } catch (e) {}
    } else {
      // Cancel any existing play promise first
      if (playPromiseRef.current) {
        playPromiseRef.current.catch(() => {
          // Ignore errors from cancelled play promises
        });
        playPromiseRef.current = null;
      }

      // Store the play promise and handle it asynchronously
      // Don't await - let the onPlay event handler update the state
      playPromiseRef.current = audio.play();

      try {
        trackContentEvent('content_play', {
          content_id: contentId ? parseInt(contentId) : null,
          content_type: 'podcast',
          content_title: content?.title || null,
          duration: duration || null,
          subject_id: content?.chapter_detail?.subject_id || null,
          chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
        });
      } catch (e) {}

      // Handle the promise asynchronously to catch errors without blocking
      playPromiseRef.current
        .then(() => {
          // Play started successfully - onPlay event will handle state update
          playPromiseRef.current = null;
        })
        .catch((error) => {
          // Handle play() errors gracefully (e.g., user interaction required, media removed, etc.)
          playPromiseRef.current = null;
          if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
            console.warn('Audio play error:', error);
          }
          // Reset state if play failed
          if (isMountedRef.current) {
            setIsPlaying(false);
          }
        });
    }
  };

  const handleSeek = (time) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = time;
      setCurrentTime(time);
      try {
        trackContentEvent('content_seek', {
          content_id: contentId ? parseInt(contentId) : null,
          content_type: 'podcast',
          from_seconds: null,
          to_seconds: time,
          subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
          chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
        });
      } catch (e) {}
    }
  };

  const handleVolumeChange = (newVolume) => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimeTracking = () => {
    if (timeTrackingIntervalRef.current) {
      clearInterval(timeTrackingIntervalRef.current);
    }

    timeTrackingIntervalRef.current = setInterval(() => {
      if (audioRef.current && isPlaying) {
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration || 1;
        const percentage = Math.round((currentTime / duration) * 100);

        // Record interaction every 10 seconds
        if (Math.round(currentTime) % 10 === 0) {
          curiosityAPI.recordInteraction({
            content: parseInt(contentId),
            interaction_type: 'viewed',
            time_spent_seconds: Math.round(currentTime),
            completion_percentage: percentage,
          }).catch(err => console.warn('Failed to record interaction:', err));

          try {
            trackContentEvent('content_time_spent', {
              content_id: contentId ? parseInt(contentId) : null,
              content_type: 'podcast',
              time_spent_seconds: Math.round(currentTime),
              completion_percentage: percentage,
              content_title: content?.title || null,
              subject_id: content?.chapter_detail?.subject_id || selectedSubjectId || null,
              chapter_id: content?.chapter_detail?.chapter_id || selectedChapterId || null,
            });
          } catch (e) {}
        }
      }
    }, 1000);
  };

  const stopTimeTracking = () => {
    if (timeTrackingIntervalRef.current) {
      clearInterval(timeTrackingIntervalRef.current);
      timeTrackingIntervalRef.current = null;
    }
  };

  // Search in transcript text
  const filteredTranscriptText = transcriptText
    ? (searchQuery
      ? transcriptText
        .split('\n')
        .filter(line => line.toLowerCase().includes(searchQuery.toLowerCase()))
        .join('\n')
      : transcriptText)
    : '';

  const handleDownloadTranscript = () => {
    if (!transcriptText) return;

    const blob = new Blob([transcriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${content?.title || 'podcast'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAskBudleeAI = () => {
    // Set transcript as prefilled text for RAG context
    if (transcriptText) {
      setPrefilledText(transcriptText);
    }
    setShowChatPanel(true);
  };

  const handleCloseChatPanel = () => {
    setShowChatPanel(false);
    setPrefilledText('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[hsl(var(--main-bg))]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--primary))] mx-auto"></div>
          <p className="text-foreground">Loading podcast...</p>
        </div>
      </div>
    );
  }

  if (!content || content.content_type !== 'podcast') {
    return (
      <div className="flex items-center justify-center h-screen bg-[hsl(var(--main-bg))]">
        <div className="text-center space-y-4">
          <p className="text-foreground">Podcast not found</p>
          <Button onClick={() => navigate('/student/curiosity')}>
            Back to Curiosity Centre
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">
      {/* Mobile StatsBar - Show at top on mobile only */}
      <div className="flex-shrink-0 pt-2 pb-2 flex justify-center lg:hidden">
        <div className="w-fit">
          <StatsBar />
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-1 h-full bg-[hsl(var(--main-bg))] overflow-hidden relative">
        {/* StatsBar - Absolutely positioned top right (desktop only) - Hide when chat panel is open (BudleeAIChatPanel already shows stats) */}
        {!showChatPanel && !isSmallScreen && (
        <div className="absolute top-4 right-8 z-40" ref={statsBarRef}>
          <StatsBar />
        </div>
      )}

      {/* Floating Animations */}
      {renderAnimations()}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col overflow-y-auto transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'pr-[400px]' : ''} ${isSmallScreen && showChatPanel ? 'h-1/2' : ''}`}>
        <div className="max-w-7xl mx-auto w-full px-6 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/student/curiosity')}
              className="text-foreground hover:bg-[hsl(var(--card-bg))] hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Curiosity Centre
            </Button>
          </div>

          {/* Audio Player - Shown First */}
          <div className="space-y-4">
            <Card ref={audioPlayerRef} className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden">
              <CardContent className="p-6 space-y-4">
                {/* Audio Controls */}
                <div className="space-y-4">
                  {/* Audio Element (hidden) */}
                  {content.audio_url ? (
                    <audio
                      ref={audioRef}
                      key={`audio-${contentId}`}
                      preload="metadata"
                      onPlay={() => {
                        if (isMountedRef.current) {
                          setIsPlaying(true);
                          startTimeTracking();
                        }
                      }}
                      onPause={() => {
                        if (isMountedRef.current) {
                          setIsPlaying(false);
                          stopTimeTracking();
                        }
                      }}
                      onError={handleAudioError}
                      onLoadedMetadata={() => {
                        console.log('Audio metadata loaded successfully');
                        if (audioRef.current && audioRef.current.duration && audioRef.current.duration > 0) {
                          setDuration(audioRef.current.duration);
                        }
                      }}
                      onLoadStart={() => {
                        console.log('Audio loading started:', content.audio_url);
                      }}
                      onCanPlay={() => {
                        console.log('Audio can play');
                      }}
                      onCanPlayThrough={() => {
                        console.log('Audio can play through');
                      }}
                      onStalled={() => {
                        console.warn('Audio stalled, trying to reload...');
                        if (audioRef.current) {
                          audioRef.current.load();
                        }
                      }}
                    >
                      {/* Use source tag for better format compatibility */}
                      <source src={content.audio_url} type={getAudioMimeType(content.audio_url)} />
                      {/* Fallback message for unsupported formats */}
                      Your browser does not support the audio format. Please try a different browser or download the audio file.
                    </audio>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      <p>Audio file not available</p>
                      <p className="text-xs mt-1">
                        {content.audio_filepath
                          ? 'Audio file exists but URL is not configured. Please contact support.'
                          : 'Please check if the audio file is uploaded'}
                      </p>
                    </div>
                  )}

                  {/* Play/Pause Button */}
                  <div className="flex items-center justify-center">
                    <Button
                      size="lg"
                      onClick={togglePlayPause}
                      disabled={!content.audio_url}
                      className="w-16 h-16 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPlaying ? (
                        <Pause className="h-8 w-8 text-white" />
                      ) : (
                        <Play className="h-8 w-8 text-white ml-1" />
                      )}
                    </Button>
                  </div>

                  {/* Time Display and Seek Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-16 text-right">
                        {formatTime(currentTime)}
                      </span>
                      <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        value={currentTime}
                        onChange={(e) => handleSeek(parseFloat(e.target.value))}
                        className="flex-1 h-2 bg-[hsl(var(--card-bg))] rounded-lg appearance-none cursor-pointer accent-[hsl(var(--primary))]"
                      />
                      <span className="text-sm text-muted-foreground w-16">
                        {formatTime(duration)}
                      </span>
                    </div>
                  </div>

                  {/* Volume Control */}
                  <div className="flex items-center gap-3">
                    <Volume2 className="h-5 w-5 text-muted-foreground" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-[hsl(var(--card-bg))] rounded-lg appearance-none cursor-pointer accent-[hsl(var(--primary))]"
                    />
                    <span className="text-sm text-muted-foreground w-12">
                      {Math.round(volume * 100)}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Title and Description - Matching Screenshot Style */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {content.tag_type && (
                <Badge
                  variant={content.tag_type === 'core' ? 'default' : 'secondary'}
                  className="px-3 py-1"
                >
                  {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                </Badge>
              )}
              <Badge variant="outline" className="px-3 py-1">
                Podcast
              </Badge>
              {isWatched && (
                <Badge variant="default" className="px-3 py-1 bg-green-500/20 text-green-400 border-green-500/50">
                  Watched
                </Badge>
              )}
              {content.audio_duration_formatted && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">{content.audio_duration_formatted}</span>
                </div>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-foreground">{content.title}</h1>
            {content.description && (
              <p className="text-muted-foreground text-xs sm:text-sm lg:text-lg max-w-3xl">{content.description}</p>
            )}
          </div>

          {/* Mobile: Quick Actions - Show above Transcript on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                  <div className="space-y-3">
                    <Button
                      className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:opacity-90 text-white font-semibold"
                      onClick={togglePlayPause}
                      disabled={!content.audio_url}
                    >
                      <Headphones className="mr-2 h-4 w-4" />
                      {isPlaying ? 'Pause Podcast' : 'Listen Podcast'}
                    </Button>
                    <Button
                      className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                      onClick={handleAskBudleeAI}
                    >
                      <Sparkles className="mr-2 h-3 w-3" />
                      Talk to Budlee
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="space-y-4">
            {/* Transcript Panel - Below Audio Player */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-[hsl(var(--teal-vivid))]" />
                    <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-foreground">Transcript</h2>
                  </div>
                  <div className="flex gap-2">
                    {transcriptText && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadTranscript}
                        title="Download transcript"
                        className="hover:bg-[hsl(var(--card-bg))]"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTranscriptPanelOpen(!transcriptPanelOpen)}
                      className="hover:bg-[hsl(var(--card-bg))]"
                    >
                      {transcriptPanelOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {transcriptPanelOpen && (
                  <div className="space-y-4">
                    {/* Search */}
                    {transcript && (
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search transcript..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-8 bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]"
                        />
                      </div>
                    )}

                    {/* Transcript Content */}
                    <div
                      ref={transcriptRef}
                      className="max-h-[400px] overflow-y-auto space-y-2 text-sm scrollbar-thin"
                    >
                      {transcriptText ? (
                        searchQuery ? (
                          filteredTranscriptText ? (
                            <p className="text-foreground whitespace-pre-wrap">{filteredTranscriptText}</p>
                          ) : (
                            <p className="text-muted-foreground text-center py-4">No matching transcript segments found for "{searchQuery}".</p>
                          )
                        ) : (
                          <p className="text-foreground whitespace-pre-wrap">{transcriptText}</p>
                        )
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No transcript available for this podcast.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Mobile: Content Info and Related Content - Show below Transcript on mobile only */}
          {isSmallScreen && (
            <div className="space-y-4">
              {/* Content Info Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Content Info</h2>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <span className="ml-2 text-foreground font-medium">Podcast</span>
                    </div>
                    {content.tag_type && (
                      <div>
                        <span className="text-muted-foreground">Category:</span>
                        <span className="ml-2 text-foreground font-medium">
                          {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                        </span>
                      </div>
                    )}
                    {content.chapter_detail?.board_name && (
                      <div>
                        <span className="text-muted-foreground">Board:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.grade_name && (
                      <div>
                        <span className="text-muted-foreground">Grade:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.subject_name && (
                      <div>
                        <span className="text-muted-foreground">Subject:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                      </div>
                    )}
                    {content.chapter_detail?.chapter_name && (
                      <div>
                        <span className="text-muted-foreground">Chapter:</span>
                        <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                      </div>
                    )}
                    {content.audio_duration_formatted && (
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="ml-2 text-foreground font-medium">{content.audio_duration_formatted}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Related Content Card */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Related Content</h2>
                  <div className="text-sm text-muted-foreground">
                    <p>Related content will be displayed here</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar - Quick Actions (shown when chat panel is closed on desktop only) */}
      {!showChatPanel && !isSmallScreen && (
        <div className="w-96 bg-[hsl(var(--sidebar-bg))] border-l border-[hsl(var(--card-border))] flex flex-col overflow-y-auto">
          <div className="p-6 space-y-4 pt-24">
            {/* Quick Actions Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <Button
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:opacity-90 text-white font-semibold"
                    onClick={togglePlayPause}
                    disabled={!content.audio_url}
                  >
                    <Headphones className="mr-2 h-4 w-4" />
                    {isPlaying ? 'Pause Podcast' : 'Listen Podcast'}
                  </Button>
                  <Button
                    className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold"
                    onClick={handleAskBudleeAI}
                  >
                    <Sparkles className="mr-2 h-3 w-3" />
                    Talk to Budlee
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Content Info Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Content Info</h2>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 text-foreground font-medium">Podcast</span>
                  </div>
                  {content.tag_type && (
                    <div>
                      <span className="text-muted-foreground">Category:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {content.tag_type === 'core' ? 'CORE' : 'GO DEEPER'}
                      </span>
                    </div>
                  )}
                  {content.chapter_detail?.board_name && (
                    <div>
                      <span className="text-muted-foreground">Board:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.board_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.grade_name && (
                    <div>
                      <span className="text-muted-foreground">Grade:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.grade_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.subject_name && (
                    <div>
                      <span className="text-muted-foreground">Subject:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.subject_name}</span>
                    </div>
                  )}
                  {content.chapter_detail?.chapter_name && (
                    <div>
                      <span className="text-muted-foreground">Chapter:</span>
                      <span className="ml-2 text-foreground font-medium">{content.chapter_detail.chapter_name}</span>
                    </div>
                  )}
                  {content.audio_duration_formatted && (
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="ml-2 text-foreground font-medium">{content.audio_duration_formatted}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Related Content Card */}
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Related Content</h2>
                <div className="text-sm text-muted-foreground">
                  <p>Related content will be displayed here</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Main content div closing tag */}
      </div>

      {/* Chat Panel - Desktop: Right Side, Mobile: Bottom Half */}
      {showChatPanel && (
        <>
          {/* Desktop Chat Panel */}
          {!isSmallScreen && (
            <div className="fixed right-0 top-0 h-screen w-[400px] z-50">
              <BudleeAIChatPanel
                onClose={handleCloseChatPanel}
                prefilledText={prefilledText}
                onClearPrefilledText={() => setPrefilledText('')}
                pageContext="curiosity_podcast"
                curiosityContentId={contentId ? parseInt(contentId) : null}
                subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
                chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
              />
            </div>
          )}

          {/* Mobile Chat Panel */}
          {isSmallScreen && (
            <div className="fixed inset-x-0 bottom-0 h-1/2 z-50 bg-[hsl(var(--main-bg))] border-t border-[hsl(var(--card-border))]">
              <BudleeAIChatPanel
                onClose={handleCloseChatPanel}
                prefilledText={prefilledText}
                onClearPrefilledText={() => setPrefilledText('')}
                pageContext="curiosity_podcast"
                curiosityContentId={contentId ? parseInt(contentId) : null}
                subjectId={content?.chapter_detail?.subject_id || selectedSubjectId || null}
                chapterId={content?.chapter_detail?.chapter_id || selectedChapterId || null}
                isMobileSplit={true}
              />
            </div>
          )}
        </>
      )}

      {/* Floating BudleeAI Mascot - Bottom Right Corner */}
      {!showChatPanel && (
        <BudleeAIIcon
          size="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20"
          iconSize="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10"
          position="fixed bottom-20 sm:bottom-7 right-4 sm:right-6 lg:bottom-8 lg:right-8"
          onClick={handleAskBudleeAI}
          showBadge={true}
        />
      )}
    </div>
  );
}

